import React, { useState } from "react";
import { makeStyles } from "@mui/styles";
import {
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  Typography,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import "./scrollBox.css";

export const textStyle = (type) => {
  let weight = 400;
  if (type === "main") {
    weight = 500;
  }

  return {
    color: "#15314E",
    fontWeight: weight,
    fontSize: "14px",
    lineHeight: "20px",
    padding: "5px 16px",
  };
};

export const statusStyle = (status) => {
  let colorToUse = "#8C7038";
  let bgColorToUse = "#FFEFD0";

  if (status === "complete") {
    colorToUse = "#5BB37F";
    bgColorToUse = "#EBF6EF";
  }

  return {
    width: "max-content",
    minWidth: "9em",
    height: "32px",
    borderRadius: 20,
    textTransform: "capitalize",
    padding: "8px 12px 8px 12px",
    display: "flex",
    alignItems: "center",
    color: colorToUse,
    backgroundColor: bgColorToUse,
    gap: "4px",
  };
};

export const useTableStyles = makeStyles({
  table: {
    minWidth: 650,
  },
  tableHead: {},
  heading: {},
  headingText: {
    fontSize: "14px",
    lineHeight: "20px",
  },
  tableRowHover: {
    transition: "background-color 0.3s ease",
    "&:hover": {
      backgroundColor: "#EBF1F8",
      cursor: "pointer",
    },
  },
});

const DynamicTable = ({
  headings = [],
  hasCheckbox = false,
  checkboxClick,
  stickyHeader = false,
  children,
  customWidth = "100%",
  scroll = "auto",
  calculateStickyPosition, // NEW PROP - Optional for backward compatibility
}) => {
  const classes = useTableStyles();
  const [sortKey, setSortKey] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  const handleSort = (index) => {
    const clickedHeading = headings?.[index];
    if (clickedHeading?.showSort) {
      setSortKey(index);
      setSortOrder(sortKey === index && sortOrder === "asc" ? "desc" : "asc");
    }
  };

  const handleCheckboxClick = () => {
    if (Boolean(checkboxClick)) {
      checkboxClick();
    }
  };

  const ariaLabel = stickyHeader ? "sticky table" : "dynamic table";

  return (
    <Table
      stickyHeader={stickyHeader}
      className={classes.table}
      aria-label={ariaLabel}
      sx={{ width: customWidth }}
      data-testid="dynamic-table-full"
    >
      <TableHead 
        className={classes.tableHead}
        sx={{
          '& th': {
            position: 'sticky',
            top: 0,
            backgroundColor: '#fff',
          },
        }}
      >
        <TableRow>
          {hasCheckbox && (
            <TableCell padding="checkbox">
              <Checkbox
                color="primary"
                inputProps={{
                  "aria-label": "select all",
                }}
                onClick={handleCheckboxClick}
              />
            </TableCell>
          )}
          {headings?.length > 0 &&
            headings?.map((heading, index) => {
              // Only calculate sticky props if function is provided
              const stickyProps = calculateStickyPosition 
                ? calculateStickyPosition(index) 
                : { zIndex: 1 };
              
              return (
                <TableCell 
                  align={heading?.align || "inherit"} 
                  key={index}
                  sx={{
                    // Only apply these styles if calculateStickyPosition exists
                    ...(calculateStickyPosition && {
                      whiteSpace: 'nowrap',
                      // minWidth: stickyProps.width || 150,
                      width: stickyProps.width || 150,
                    }),
                    backgroundColor: '#fff',
                    ...(stickyProps.right !== undefined && {
                      position: 'sticky',
                      right: stickyProps.right,
                      zIndex: (stickyProps.zIndex || 1) + 1,
                      boxShadow: stickyProps.boxShadow,
                      width:stickyProps.width || 150,
                    }),
                  }}
                >
                  <Typography
                    className={classes.headingText}
                    fontWeight={600}
                    color={"primary.darker"}
                    textTransform={"capitalize"}
                  >
                    {heading?.name || heading?.label || heading}
                  </Typography>
                  {heading?.showSort && (
                    <ExpandMoreRoundedIcon
                      onClick={() => handleSort(index)}
                      style={{
                        color: sortKey === index ? "#3374B9" : "#3374B9",
                        transform:
                          sortKey === index && sortOrder === "asc"
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                      }}
                    />
                  )}
                </TableCell>
              );
            })}
        </TableRow>
      </TableHead>
      <TableBody>{children}</TableBody>
    </Table>
  );
};

export default DynamicTable;